import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Button, Dropdown, Form, Modal } from '@/components/ui';
import { FleetStatus, FLEET_STATUS_LABELS, FleetStatusImpact, Vehicle } from '../../../types/Vehicle';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import { vehicleTransferService, type DestinationArea, type TransferDetail } from '../../../services/vehicleTransferService';
import { todayPacific } from '../../../utils/wrapDate';
import { fleetStatusLabel, isActiveUnit, serviceAreaLabel, unitAvailabilityLabel } from '../utils/equipmentDisplay';
import RequestTransferModal from './RequestTransferModal';

interface EquipmentUnitActionsProps {
  vehicle: Vehicle;
  canEdit: boolean;
  editing: boolean;
  canSetAvailability: boolean;
  canSetFleetStatus: boolean;
  canTransferForemanArea: boolean;
  /** May request a service-area transfer (it then goes for approval). */
  canTransferServiceArea: boolean;
  /** Admin correction: move the unit at once, with no approval. */
  canMoveServiceAreaNow: boolean;
  /** The unit's open transfer, if any: a second request is refused. */
  openTransferId: number | null;
  onTransferRequested: (transfer: TransferDetail) => void;
  onEdit: () => void;
  onUpdated: (vehicle: Vehicle) => void;
}

type Dialog = 'availability' | 'fleet' | 'foreman' | 'service' | 'request' | null;

/** Room kept beside the inline actions before they fold into More, so they never touch the edge. */
const FOLD_MARGIN = 16;

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">{message}</p> : null;

const Required = () => <span className="tw-text-danger" aria-hidden="true"> *</span>;

/**
 * Unit header actions (EQ-24): Edit and Set Availability stay visible; Set Equipment Status and
 * the transfers go into a labelled More menu where the row is short. Actions the user lacks
 * permission for are hidden (ACT-06), and a unit that isn't Active offers no availability or
 * transfer action (EQ-10). Every dialog shows its errors under the field (FORM-01).
 */
const EquipmentUnitActions: React.FC<EquipmentUnitActionsProps> = ({
  vehicle,
  canEdit,
  editing,
  canSetAvailability,
  canSetFleetStatus,
  canTransferForemanArea,
  canTransferServiceArea,
  canMoveServiceAreaNow,
  openTransferId,
  onTransferRequested,
  onEdit,
  onUpdated
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [availability, setAvailability] = useState('InService');
  const [reason, setReason] = useState('');
  const [estimatedReturn, setEstimatedReturn] = useState('');
  const [status, setStatus] = useState(vehicle.status);
  const [foremanAreaId, setForemanAreaId] = useState('');
  const [serviceAreaId, setServiceAreaId] = useState('');
  const [note, setNote] = useState('');
  const [destinations, setDestinations] = useState<DestinationArea[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [impact, setImpact] = useState<FleetStatusImpact | null>(null);
  const [impactError, setImpactError] = useState<string | null>(null);

  const active = isActiveUnit(vehicle);
  const currentAreaIds = (vehicle.vehicleServiceAreas ?? []).map((sa) => sa.serviceAreaId ?? sa.serviceArea.id);
  const currentAreas = (vehicle.vehicleServiceAreas ?? []).map((sa) => serviceAreaLabel(sa.serviceArea)).join(', ');

  // Measured, not a breakpoint (EQ-24): the hidden copy below holds every action inline, so its
  // width is what one row needs. When the space is narrower, the secondary actions go into More.
  const measureRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const space = rowRef.current?.parentElement;
    const needed = measureRef.current;
    if (!space || !needed || typeof ResizeObserver === 'undefined') return undefined;
    const check = () => setCompact(needed.scrollWidth + FOLD_MARGIN > space.clientWidth);
    const observer = new ResizeObserver(check);
    observer.observe(space);
    observer.observe(needed);
    check();
    return () => observer.disconnect();
  }, []);

  const needsDestinations = dialog === 'foreman' || dialog === 'service';
  useEffect(() => {
    if (!needsDestinations || destinations) return;
    vehicleTransferService
      .destinations()
      .then(setDestinations)
      .catch(() => setErrors({ _form: 'Could not load service and foreman areas. Close and try again.' }));
  }, [needsDestinations, destinations]);

  const deactivating = status !== FleetStatus.Active && status !== 'Active';
  useEffect(() => {
    if (dialog !== 'fleet' || !deactivating) {
      setImpact(null);
      setImpactError(null);
      return undefined;
    }
    let cancelled = false;
    vehicleService
      .getFleetStatusImpact(vehicle.id)
      .then((data) => !cancelled && setImpact(data))
      .catch(() => !cancelled && setImpactError('Could not check open assignments or daily logs. Review them before deactivating.'));
    return () => {
      cancelled = true;
    };
  }, [dialog, deactivating, vehicle.id]);

  const open = (next: Dialog) => {
    setErrors({});
    setReason('');
    setNote('');
    setEstimatedReturn('');
    setForemanAreaId('');
    setServiceAreaId('');
    setAvailability(vehicle.availability === 'InService' ? 'OutOfService' : 'InService');
    setStatus(vehicle.status);
    setDialog(next);
  };
  const close = () => {
    setDialog(null);
    setErrors({});
  };

  const run = async (validate: () => Record<string, string>, fn: () => Promise<Vehicle>) => {
    const local = validate();
    setErrors(local);
    if (Object.keys(local).length > 0) return;
    setSaving(true);
    try {
      onUpdated(await fn());
      close();
    } catch (err) {
      setErrors(fieldErrorsOf(err));
    } finally {
      setSaving(false);
    }
  };

  const serviceDestinations = (destinations ?? []).filter((area) => !(currentAreaIds.length === 1 && currentAreaIds[0] === area.id));
  const chosenArea = destinations?.find((area) => String(area.id) === serviceAreaId);

  const showAvailability = canSetAvailability && active;
  const showRequest = canTransferServiceArea && active && !openTransferId;
  const secondary: Array<{ key: string; label: string; onClick: () => void }> = [
    ...(canSetFleetStatus ? [{ key: 'fleet', label: 'Set Equipment Status', onClick: () => open('fleet') }] : []),
    ...(canTransferForemanArea && active ? [{ key: 'foreman', label: 'Transfer Foreman Area', onClick: () => open('foreman') }] : []),
    ...(showRequest ? [{ key: 'request', label: 'Transfer Service Area', onClick: () => open('request') }] : []),
    ...(canMoveServiceAreaNow && active ? [{ key: 'service', label: 'Move Now (No Approval)', onClick: () => open('service') }] : [])
  ];

  const inlineButtons = (measuring: boolean) => (
    <>
      {secondary.map((item) => (
        <Button
          key={item.key}
          size="sm"
          variant="outline-secondary"
          className="tw-whitespace-nowrap"
          tabIndex={measuring ? -1 : undefined}
          onClick={measuring ? undefined : item.onClick}
        >
          {item.label}
        </Button>
      ))}
    </>
  );

  return (
    <div ref={rowRef} className="tw-relative tw-flex tw-min-w-0 tw-flex-nowrap tw-items-start tw-justify-end tw-gap-2">
      <div
        ref={measureRef}
        aria-hidden="true"
        className="tw-pointer-events-none tw-invisible tw-absolute tw-right-0 tw-top-0 tw-flex tw-w-max tw-flex-nowrap tw-gap-2"
      >
        {inlineButtons(true)}
        {showAvailability && <Button size="sm" variant="outline-secondary" tabIndex={-1}>Set Availability</Button>}
        {canEdit && !editing && <Button size="sm" tabIndex={-1}>Edit</Button>}
      </div>
      {secondary.length > 0 &&
        (compact ? (
          <Dropdown align="end">
            <Dropdown.Toggle className="tw-inline-flex tw-h-8 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-text-sm">
              More
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {secondary.map((item) => (
                <Dropdown.Item key={item.key} onClick={item.onClick}>
                  {item.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          inlineButtons(false)
        ))}
      {showAvailability && (
        <Button size="sm" variant="outline-secondary" className="tw-whitespace-nowrap" onClick={() => open('availability')}>
          Set Availability
        </Button>
      )}
      {canEdit && !editing && (
        <Button size="sm" className="tw-whitespace-nowrap" onClick={onEdit}>
          <i className="bi bi-pencil tw-mr-1" aria-hidden="true" />
          Edit
        </Button>
      )}

      <RequestTransferModal
        vehicleId={vehicle.id}
        show={dialog === 'request'}
        onHide={close}
        onCreated={(transfer) => {
          close();
          onTransferRequested(transfer);
        }}
      />

      <Modal show={dialog === 'availability'} onHide={close} size="sm">
        <Modal.Header closeButton><Modal.Title>Set Availability</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm tw-text-content-secondary">Current: {unitAvailabilityLabel(vehicle)}</p>
          <Form.Label htmlFor="set-availability">Availability<Required /></Form.Label>
          <Form.Select id="set-availability" value={availability} onChange={(e) => setAvailability(e.target.value)}>
            <option value="InService">In Service</option>
            <option value="OutOfService">Out of Service</option>
          </Form.Select>
          {availability === 'OutOfService' && (
            <>
              <Form.Label htmlFor="availability-reason" className="tw-mt-3">Reason<Required /></Form.Label>
              <Form.Control id="availability-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              <FieldError message={errors.reason} />
              <Form.Label htmlFor="availability-return" className="tw-mt-3">Expected Return</Form.Label>
              <Form.Control
                id="availability-return"
                type="date"
                min={todayPacific()}
                value={estimatedReturn}
                onChange={(e) => setEstimatedReturn(e.target.value)}
              />
              <FieldError message={errors.estimatedReturnToServiceDate} />
            </>
          )}
          <FieldError message={errors._form} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button
            disabled={saving}
            onClick={() =>
              run(
                () => {
                  const e: Record<string, string> = {};
                  if (availability === 'OutOfService' && !reason.trim()) e.reason = 'Reason required';
                  if (estimatedReturn && estimatedReturn < todayPacific()) e.estimatedReturnToServiceDate = "Expected return can't be before today";
                  return e;
                },
                () =>
                  vehicleService.setAvailability(vehicle.id, {
                    availability,
                    reason: reason.trim() || undefined,
                    estimatedReturnToServiceDate: availability === 'OutOfService' ? estimatedReturn || null : null
                  })
              )
            }
          >
            {saving ? 'Saving…' : 'Apply'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialog === 'fleet'} onHide={close} size="sm">
        <Modal.Header closeButton><Modal.Title>Set Equipment Status</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm tw-text-content-secondary">Current: {fleetStatusLabel(vehicle.status)}</p>
          <p className="tw-text-sm tw-text-content-secondary">Units that aren&apos;t Active can&apos;t be put in service or transferred.</p>
          <Form.Label htmlFor="fleet-status">Equipment Status<Required /></Form.Label>
          <Form.Select id="fleet-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {Object.values(FleetStatus).map((value) => (
              <option key={value} value={value}>{FLEET_STATUS_LABELS[value]}</option>
            ))}
          </Form.Select>
          <FieldError message={errors.status} />
          <Form.Label htmlFor="fleet-reason" className="tw-mt-3">Reason<Required /></Form.Label>
          <Form.Control id="fleet-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <FieldError message={errors.reason} />
          {deactivating && (
            <p className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-content-secondary">
              This hides the unit from driver pickers straight away. Making it Active again doesn&apos;t show it to drivers while it is out of
              service or its insurance has lapsed.
            </p>
          )}
          {deactivating && (impactError || (impact && impact.warnings.length > 0)) && (
            <Alert variant="warning" className="tw-mt-3 tw-mb-0">
              {impactError}
              {(impact?.warnings || []).map((warning) => (
                <p key={warning} className="tw-mb-0">{warning}</p>
              ))}
              <p className="tw-mb-0 tw-mt-2">This is a warning – you can still continue.</p>
            </Alert>
          )}
          <FieldError message={errors._form} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button
            disabled={saving}
            onClick={() =>
              run(
                (): Record<string, string> => (reason.trim() ? {} : { reason: 'Reason required' }),
                () => vehicleService.setFleetStatus(vehicle.id, { status, reason: reason.trim() })
              )
            }
          >
            {saving ? 'Saving…' : 'Apply'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialog === 'foreman'} onHide={close} size="sm">
        <Modal.Header closeButton><Modal.Title>Transfer Foreman Area</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm tw-text-content-secondary">Current: {vehicle.foremanArea?.name || 'Unassigned'}</p>
          <Form.Label htmlFor="foreman-destination">Destination<Required /></Form.Label>
          <Form.Select id="foreman-destination" value={foremanAreaId} onChange={(e) => setForemanAreaId(e.target.value)}>
            <option value="">Select a Foreman Area</option>
            {(destinations ?? []).map((area) => (
              <optgroup key={area.id} label={serviceAreaLabel(area)}>
                {area.foremanAreas
                  .filter((fa) => fa.id !== vehicle.foremanArea?.id)
                  .map((fa) => (
                    <option key={fa.id} value={fa.id}>{fa.name}</option>
                  ))}
              </optgroup>
            ))}
          </Form.Select>
          <FieldError message={errors.foremanAreaId} />
          <Form.Label htmlFor="foreman-note" className="tw-mt-3">Note</Form.Label>
          <Form.Control id="foreman-note" value={note} onChange={(e) => setNote(e.target.value)} />
          <FieldError message={errors._form} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button
            disabled={saving}
            onClick={() =>
              run(
                (): Record<string, string> => (foremanAreaId ? {} : { foremanAreaId: 'Destination foreman area required' }),
                () => vehicleService.transferForemanArea(vehicle.id, { foremanAreaId: Number(foremanAreaId), note: note.trim() || undefined })
              )
            }
          >
            {saving ? 'Saving…' : 'Transfer'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialog === 'service'} onHide={close}>
        <Modal.Header closeButton><Modal.Title>Move Now (No Approval)</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="tw-mb-2 tw-text-sm tw-text-warning-text">
            For correcting data only. This skips the transfer approvals and Finance, and costing changes from this date forward. To move a
            unit, use Transfer Service Area.
          </p>
          <p className="tw-text-sm tw-text-content-secondary">
            Current: {currentAreas || 'No service area'}
            {vehicle.foremanArea ? `, ${vehicle.foremanArea.name}` : ''}
          </p>
          <Form.Label htmlFor="move-service-area">Destination Service Area<Required /></Form.Label>
          <Form.Select
            id="move-service-area"
            value={serviceAreaId}
            onChange={(e) => {
              setServiceAreaId(e.target.value);
              setForemanAreaId('');
            }}
          >
            <option value="">Select a Service Area</option>
            {serviceDestinations.map((area) => (
              <option key={area.id} value={area.id}>{serviceAreaLabel(area)}</option>
            ))}
          </Form.Select>
          <FieldError message={errors.serviceAreaId} />
          <Form.Label htmlFor="move-foreman-area" className="tw-mt-3">Destination Foreman Area<Required /></Form.Label>
          <Form.Select
            id="move-foreman-area"
            value={foremanAreaId}
            disabled={!chosenArea}
            onChange={(e) => setForemanAreaId(e.target.value)}
          >
            <option value="">{chosenArea ? 'Select a Foreman Area' : 'Choose a Service Area First'}</option>
            {(chosenArea?.foremanAreas ?? []).map((fa) => (
              <option key={fa.id} value={fa.id}>{fa.name}</option>
            ))}
          </Form.Select>
          <FieldError message={errors.foremanAreaId} />
          <Form.Label htmlFor="move-note" className="tw-mt-3">Note</Form.Label>
          <Form.Control id="move-note" value={note} onChange={(e) => setNote(e.target.value)} />
          <FieldError message={errors._form} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button
            disabled={saving}
            onClick={() =>
              run(
                () => {
                  const e: Record<string, string> = {};
                  if (!serviceAreaId) e.serviceAreaId = 'Destination service area required';
                  if (!foremanAreaId) e.foremanAreaId = 'Destination foreman area required';
                  return e;
                },
                () =>
                  vehicleService.transferServiceArea(vehicle.id, {
                    serviceAreaId: Number(serviceAreaId),
                    foremanAreaId: Number(foremanAreaId),
                    note: note.trim() || undefined
                  })
              )
            }
          >
            {saving ? 'Saving…' : 'Move Unit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EquipmentUnitActions;
